import type { ScheduledPaymentPolicy, PrepaidMethod } from "@/lib/scheduling/types"
import { bool, int, oneOf, record, text } from "@/lib/scheduling/wire"

/** Why a day carries no slot, or the whole horizon is empty (§9.3). */
export type ScheduleEmptyReason =
  | "STORE_CLOSED"
  | "NO_INTERSECTION"
  | "SCHEDULING_DISABLED"
  | "OUTSIDE_HORIZON"
  | "NO_ETA"
  | ""

export const SCHEDULE_EMPTY_REASONS: ScheduleEmptyReason[] = [
  "STORE_CLOSED",
  "NO_INTERSECTION",
  "SCHEDULING_DISABLED",
  "OUTSIDE_HORIZON",
  "NO_ETA",
  "",
]

/**
 * One offered slot. `arrival*` is the promise to the customer (a WINDOW, not an
 * instant); `ready*` is the kitchen's, and the only thing the payload echoes as
 * `schedule` (D3).
 */
export type ScheduleSlot = {
  arrivalStart: string
  arrivalEnd: string
  readyStart: string
  readyEnd: string
}

/** A bag line that costs the day its slots; exactly one id identifies it. */
export type BlockingLine = {
  itemOfferId: string | null
  optionId: string | null
  reason: ScheduleEmptyReason
}

/** One calendar day of the horizon, on the tenant's clock. */
export type ScheduleDay = {
  date: string
  slots: ScheduleSlot[]
  blockingLines: BlockingLine[]
}

/** Response of `GET /public/merchants/:id/schedule-slots` (§9.3). */
export type ScheduleSlots = {
  days: ScheduleDay[]
  etaMinutes: number
  timezone: string
  payment: ScheduledPaymentPolicy
  emptyReason: ScheduleEmptyReason
}

const PREPAID_METHODS: PrepaidMethod[] = ["PIX_ONLINE", "CARD_ONLINE"]

function parseSlot(raw: unknown): ScheduleSlot | null {
  const source = record(raw)
  if (!source) return null
  const arrivalStart = text(source.arrivalStart)
  const readyStart = text(source.readyStart)
  if (!arrivalStart || !readyStart) return null
  return {
    arrivalStart,
    arrivalEnd: text(source.arrivalEnd) || arrivalStart,
    readyStart,
    readyEnd: text(source.readyEnd) || readyStart,
  }
}

function parseBlockingLine(raw: unknown): BlockingLine | null {
  const source = record(raw)
  if (!source) return null
  const itemOfferId = text(source.itemOfferId) || null
  const optionId = text(source.optionId) || null
  if (!itemOfferId && !optionId) return null
  return {
    itemOfferId,
    optionId,
    reason: oneOf(source.reason, SCHEDULE_EMPTY_REASONS, ""),
  }
}

function parseDay(raw: unknown): ScheduleDay | null {
  const source = record(raw)
  if (!source) return null
  const date = text(source.date)
  if (!date) return null

  const slots = Array.isArray(source.slots) ? source.slots : []
  const blocking = Array.isArray(source.blockingLines) ? source.blockingLines : []

  return {
    date,
    slots: slots.map(parseSlot).filter((slot): slot is ScheduleSlot => slot !== null),
    blockingLines: blocking
      .map(parseBlockingLine)
      .filter((line): line is BlockingLine => line !== null),
  }
}

function parsePayment(raw: unknown): ScheduledPaymentPolicy {
  const source = record(raw)
  const prepaid = Array.isArray(source?.prepaidMethods) ? source.prepaidMethods : []
  const pending = Array.isArray(source?.pendingMethodIds) ? source.pendingMethodIds : []
  return {
    prepaymentRequired: bool(source?.prepaymentRequired),
    prepaidMethods: prepaid.filter((method): method is PrepaidMethod =>
      PREPAID_METHODS.includes(method as PrepaidMethod),
    ),
    pendingMethodIds: pending.filter((id): id is string => typeof id === "string"),
  }
}

/** Narrows the untyped slots payload; unknown enum values fall back to "". */
export function parseScheduleSlots(raw: unknown): ScheduleSlots | null {
  const source = record(raw)
  if (!source) return null
  const days = Array.isArray(source.days) ? source.days : []

  return {
    days: days.map(parseDay).filter((day): day is ScheduleDay => day !== null),
    etaMinutes: int(source.etaMinutes),
    timezone: text(source.timezone),
    payment: parsePayment(source.payment),
    emptyReason: oneOf(source.emptyReason, SCHEDULE_EMPTY_REASONS, ""),
  }
}

/** Days the customer may pick: only those that actually carry a slot (D4). */
export function offeredDays(slots: ScheduleSlots | null): ScheduleDay[] {
  return slots?.days.filter((day) => day.slots.length > 0) ?? []
}

/** The day the picker should land on: the chosen one, else the first offered. */
export function resolveSelectedDay(
  slots: ScheduleSlots | null,
  chosenDate: string | null,
): ScheduleDay | null {
  const days = offeredDays(slots)
  if (days.length === 0) return null
  return days.find((day) => day.date === chosenDate) ?? days[0]
}

/**
 * Lines blocking a day, whichever day the customer is looking at. Read from the
 * raw days (not the offered ones) so a day with zero slots still explains why.
 */
export function blockingLinesForDate(
  slots: ScheduleSlots | null,
  date: string | null,
): BlockingLine[] {
  if (!slots || !date) return []
  return slots.days.find((day) => day.date === date)?.blockingLines ?? []
}

/** Resolves a blocking line to the name the customer sees in their bag. */
export type BlockingLineNamer = (line: BlockingLine) => string | null

/**
 * Banner when a bag line blocks the chosen day (section 9.2 item 3). Null when
 * nothing blocks or no blocking line can be named.
 */
export function formatBlockingBanner(
  lines: BlockingLine[],
  nameOf: BlockingLineNamer,
): string | null {
  const names = [...new Set(lines.map(nameOf).filter((name): name is string => Boolean(name)))]
  if (names.length === 0) return null

  const subject =
    names.length === 1 ? names[0] : `${names.slice(0, -1).join(", ")} e ${names[names.length - 1]}`
  const verb = names.length === 1 ? "não está disponível" : "não estão disponíveis"

  return `${subject} ${verb} neste dia — remova ou escolha outro dia`
}
